<template>
  <nav v-show="count > limit && links.length" class="font-semibold leading-none">
    <ul class="flex flex-wrap justify-between sm:justify-center items-center">
      <!-- Reduce by one page -->
      <li v-if="enablePrevious" class="c-pagination-list-item c-pagination-list-item--browse justify-start sm:justify-center" @click="onClick(currentPage - 1)">
        <div class="c-pagination-list-item__content group bg-white cursor-pointer">
          <AtomIconChevronLeft class="w-4 shrink-0" />
        </div>
      </li>

      <li
        v-for="(link, index) in links"
        :key="index"
        class="c-pagination-list-item justify-center"
        :class="{ 'c-pagination-list-item--dots': link.type === 'dots' }"
        @click="onClick(link.page)"
      >
        <div
          :class="{
            'pointer-events-none': link.type === 'dots',
            'c-pagination-list-item__content group cursor-pointer': link.type === 'link',
            'c-pagination-list-item__content bg-app-primary-1-orange--bittersweet text-white': link.page === currentPage,
          }"
        >
          <span class="c-pagination-page-number">{{ link.type === 'dots' ? '...' : link.page }}</span>
        </div>
      </li>

      <!-- Increase by one page -->
      <li v-if="enableNext" class="c-pagination-list-item justify-end sm:justify-center" @click="onClick(currentPage + 1)">
        <div class="c-pagination-list-item__content group bg-white cursor-pointer">
          <AtomIconChevronRight class="w-4 shrink-0" />
        </div>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
const props = defineProps({
  count: { type: Number, required: true, validator: (page: number) => page >= 0 },
  limit: { type: Number, required: true, validator: (page: number) => page > 0 },
  page: { type: Number, required: true, validator: (page: number) => page > 0 },
  showPrevious: { type: Boolean, required: false, default: true },
  showNext: { type: Boolean, required: false, default: true },
})

const emit = defineEmits(['click'])

const highestPage = computed(() => Math.ceil(props.count / props.limit))
const currentPage = computed(() => Math.min(unref(highestPage), props.page))
const enablePrevious = computed(() => props.showPrevious && unref(currentPage) >= 2)
const enableNext = computed(() => props.showNext && unref(currentPage) < unref(highestPage))
/** One pagination entry: a page link, or an ellipsis standing in for a range of pages. */
type PaginationLink = { type: string; page?: number }

const links = computed<PaginationLink[]>(() => {
  const currentPageValue = unref(currentPage)
  const highestPageValue = unref(highestPage)
  if (highestPageValue < 6) {
    // only the first 5 links are needed
    return [...Array(highestPageValue).keys()].map((offset) => ({ type: 'link', page: offset + 1 }))
  } else if (currentPageValue > 3 && currentPageValue < highestPageValue - 2) {
    // replace links with dots at the beginning and end
    return [{ type: 'dots' }, ...[-1, 0, 1].map((offset) => ({ type: 'link', page: currentPageValue + offset })), { type: 'dots' }]
  } else if (currentPageValue <= highestPageValue / 2) {
    // replace links with dots at the end
    return [...[1, 2, 3, 4].map((offset) => ({ type: 'link', page: offset })), { type: 'dots' }]
  } else {
    // replace links with dots at the beginning
    return [{ type: 'dots' }, ...[3, 2, 1, 0].map((offset) => ({ type: 'link', page: highestPageValue - offset }))]
  }
})

// Emit a 'click' event to switch to a different page
const onClick = (targetPage?: number) => {
  if (targetPage && targetPage !== unref(currentPage)) {
    emit('click', { page: Math.max(1, Math.min(unref(highestPage), unref(targetPage))) })
    window.scrollTo(0, 0)
  }
}
</script>

<style lang="scss" scoped>
.c-pagination-list-item {
  @apply flex mx-1;

  @screen sm {
    @apply w-12 mx-1;
  }

  &.c-pagination-list-item--browse,
  &.c-pagination-list-item--dots {
    @apply w-auto;
  }
}

.c-pagination-list-item__content {
  @apply inline-flex justify-center items-center rounded-lg shrink-0 transform transition duration-200;
  width: 28px;
  height: 28px;

  &:hover {
    @apply bg-black text-white;
  }
}

.c-pagination-page-number {
  margin-top: 2px;
}
</style>
